import { Button } from '@/global/components/Button'
import GridToolbarContainer from '@/global/components/DataGrid/components/CustomGridToolBar/Container'
import ToolbarDensitySelector from '@/global/components/DataGrid/components/CustomGridToolBar/DensitySelector'
import {
    GridButton,
    GridMenuButton,
} from '@/global/components/DataGrid/components/CustomGridToolBar/MenuComponents'
import { useTableApiContext } from '@/global/components/DataGrid/utils/table-context'
import Menu from '@/global/components/Menu'
import { Tooltip } from '@/global/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { useStore } from '@/store'
import { CategoriaEnum, separateWords } from '@acme/auth'
import { PeriodoPontoFactory } from '@acme/common'
import { SN } from '@acme/core'
import dayjs from 'dayjs'
import { useAuth } from 'hooks'
import {
    Calendar1Icon,
    CalendarClockIcon,
    DownloadIcon,
    ListFilterIcon,
    ListIcon,
} from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { jornada, JornadaDiariaQueryXor } from 'service/api'
import { AjustePontoButton } from '../buttons/AjustePonto'
import ButtonCalculo from '../buttons/CalculoButton'
import { useDigitacaoColumns } from '../columns'
import { useDigitacaoContext } from '../pages/DigitacaoLayout'
import DigitacaoView, { ViewMode } from '../toggle-view'
import { maximoRegistros } from '../utils/exportacao'
import { useDigitacaoStore } from '../utils/store'

interface IProps {
    view: ViewMode
    periodo: [string, string]
}

export default function DigitacaoToolbar({ view, periodo }: IProps) {
    const horarioNoturno = useStore(state => state.employee?.horarioNoturno)
    const empresa = useStore(state => state.company)

    const MemoizedCalculo = useMemo(() => {
        if (!empresa) return null
        const [competencia] = PeriodoPontoFactory.fromPeriodo(
            ...periodo,
            SN.isTrue(horarioNoturno),
        ).getCompetencias({
            diaInicioPonto: empresa.diaInicioPonto,
            trocasInicioPonto: empresa.TrocaInicioPonto,
        })
        return <ButtonCalculo competencia={competencia} view={view} />
    }, [periodo, view, empresa])

    return (
        <GridToolbarContainer className="gap-1">
            <div className="inline-flex items-center gap-2">
                <ToolbarResponsiva />
                <LabelPeriodo />
            </div>
            <div className="inline-flex items-center gap-2 flex-wrap">
                {MemoizedCalculo}
                <AjustePontoButton />
                <DigitacaoView />
            </div>
        </GridToolbarContainer>
    )
}

export type TFiltrosDigitacao = 'funcionario' | 'locais' | 'centros-custo'

/** Filtro persistido no storage (default: por funcionário). */
export function useFiltrosDigitacao() {
    const filtro = useDigitacaoStore(state => state.filtro)
    const setFiltro = useDigitacaoStore(state => state.setFiltro)
    return [filtro, setFiltro] as const
}

function ExportarCsvAssinado({ hideLabel }: { hideLabel?: boolean }) {
    const { view, queryParams } = useDigitacaoContext()
    const { columns } = useDigitacaoColumns(view)
    const gridData = jornada.diaria.getQueryData(queryParams as any) ?? []

    const handleExport = () => {
        const maxRegistros = maximoRegistros(gridData as any[])

        function getCellValue(id: any, field: any) {
            const rowData = gridData.find((item: any) => item.id === id)
            if (!rowData) return ''
            let value = (rowData as any)[field]
            const col = columns.find(column => column.field === field)
            if (field === 'registros') {
                const horarios = rowData.registros.map(
                    registro => registro.hora,
                )
                if (horarios.length < maxRegistros)
                    horarios.push(
                        ...Array(maxRegistros - horarios.length).fill(''),
                    )
                return horarios.map(it => `"${it || ''}"`).join(';')
            }
            if (col?.valueGetter)
                value = col.valueGetter({
                    row: rowData,
                    value,
                    field,
                    id,
                } as any)
            if (field === 'ausencias') {
                const ausencias = Object.entries(value ?? {})
                    .filter(([_, v]) => !!v)
                    .map(([k]) => separateWords(k))
                    .join(', ')
                return `"${ausencias}"` as any
            }
            if (col?.valueFormatter)
                value = (() => {
                    try {
                        return col.valueFormatter({
                            value,
                            row: rowData,
                            field,
                            id,
                        } as any)
                    } catch (_) {
                        return value
                    }
                })()
            if (value === null || value === undefined) return ''

            return `"${value}"` as any
        }

        const exportPromise = new Promise((resolve, reject) => {
            try {
                const headers = columns
                    .filter(column => !column.disableExport)
                    .map(({ headerName, field }) => [headerName, field])
                const csvContent = [
                    headers
                        .map(([header]) => `${header}`)
                        .join(';')
                        .replace(
                            'Registros',
                            Array.from({ length: maxRegistros })
                                .map((_, index) =>
                                    index % 2 === 0 ? 'Entrada' : 'Saída',
                                )
                                .join(';'),
                        ),
                    ...gridData.map(({ id }) =>
                        headers
                            .map(([, field]) => getCellValue(id, field))
                            .join(';'),
                    ),
                ].join('\n')

                const csvDataUri = `data:text/csv;charset=utf-8,${encodeURIComponent(
                    csvContent,
                )}`
                const link = document.createElement('a')
                link.setAttribute('href', csvDataUri)
                link.setAttribute(
                    'download',
                    `exportacao-digitacao-diaria-${
                        (queryParams as JornadaDiariaQueryXor).dia
                    }`,
                )
                link.click()

                resolve(true)
            } catch (error) {
                reject(error)
            }
        })

        return exportPromise
    }

    return (
        <GridButton
            onClick={handleExport}
            startIcon={<DownloadIcon />}
            label={hideLabel ? undefined : 'Exportar CSV'}
        />
    )
}

function LabelPeriodo() {
    const { periodo, view } = useDigitacaoContext()

    const label =
        view === 'diario' || periodo[0] === periodo[1]
            ? dayjs(periodo[1])
                  .format('dddd[,] DD [de] MMMM [de] YYYY')
                  .replace(/^./, str => str.toUpperCase())
            : `Período de ${dayjs(periodo[0]).format('DD/MM/YYYY')} a ${dayjs(
                  periodo[1],
              ).format('DD/MM/YYYY')}`

    return (
        <span className="text-start sm:text-center text-sm sm:text-base">
            {label}
        </span>
    )
}

function ToolbarResponsiva() {
    const { tipoFiltro, changeTipoFiltro } = useDigitacaoContext()
    const {
        decoded: { categoria },
    } = useAuth()
    const [organizar, setOrganizar] = useDigitacaoStore(state => [
        state.organizacao,
        state.setOrganizar,
    ])
    const { view } = useDigitacaoContext()

    const { density } = useTableApiContext()
    const setDensidade = useDigitacaoStore(state => state.setDensidade)
    useEffect(() => {
        if (density) setDensidade(density)
    }, [density])

    const MenuItemsFiltro = [
            <Menu.Item
                key="mi-todos"
                onClick={() => changeTipoFiltro('funcionario')}
                selected={tipoFiltro === 'funcionario'}
            >
                {CircleCheck(tipoFiltro === 'funcionario')}
                <div className="flex flex-col ml-2">
                    <span>Por funcionário</span>
                    <span className="text-xs max-w-52 whitespace-break-spaces text-muted-foreground">
                        Exibe todos os funcionários, sem filtro adicional
                    </span>
                </div>
            </Menu.Item>,
            <Menu.Item
                key="mi-locais"
                onClick={() => changeTipoFiltro('locais')}
                selected={tipoFiltro === 'locais'}
            >
                {CircleCheck(tipoFiltro === 'locais')}
                <div className="flex flex-col ml-2">
                    <span>Por local</span>
                    <span className="text-xs max-w-52 whitespace-break-spaces text-muted-foreground">
                        Possibilita filtrar funcionários por local de trabalho
                    </span>
                </div>
            </Menu.Item>,
            <Menu.Item
                key="mi-cc"
                onClick={() => changeTipoFiltro('centros-custo')}
                selected={tipoFiltro === 'centros-custo'}
            >
                {CircleCheck(tipoFiltro === 'centros-custo')}
                <div className="flex flex-col ml-2">
                    <span>Por centro de custo</span>
                    <span className="text-xs max-w-52 whitespace-break-spaces text-muted-foreground">
                        Possibilita filtrar funcionários por centro de custo
                    </span>
                </div>
            </Menu.Item>,
        ],
        MenuItemsOrganizar = [
            <Menu.Item
                key="mi-dia"
                onClick={() => setOrganizar('dia')}
                selected={organizar === 'dia'}
            >
                {CircleCheck(organizar === 'dia')}
                <div className="flex flex-col ml-2">
                    <span>Por dia</span>
                    <span className="text-xs max-w-52 whitespace-break-spaces text-muted-foreground">
                        Os registros são exibidos conforme o dia da marcação
                    </span>
                </div>
            </Menu.Item>,
            <Menu.Item
                key="mi-jornada"
                onClick={() => setOrganizar('jornada')}
                selected={organizar === 'jornada'}
            >
                {CircleCheck(organizar === 'jornada')}
                <div className="flex flex-col ml-2">
                    <span>Por jornada</span>
                    <span className="text-xs max-w-52 whitespace-break-spaces text-muted-foreground">
                        Os registros são deslocados conforme a jornada do
                        funcionário
                    </span>
                </div>
            </Menu.Item>,
        ]

    return [
        <Menu.Root className="flex xl:hidden" key="mobile">
            <Menu.Trigger>
                <Button
                    variant="outlined"
                    title="Mostrar menu"
                    className="text-nowrap gap-2"
                    startIcon={<ListIcon />}
                >
                    Opções
                </Button>
            </Menu.Trigger>
            <Menu.Popover>
                {categoria !== CategoriaEnum.Funcionario && (
                    <GridMenuButton
                        key="filtro"
                        startIcon={<ListFilterIcon />}
                        className="min-w-full"
                        label="Filtrar"
                    >
                        {MenuItemsFiltro}
                    </GridMenuButton>
                )}
                <ToolbarDensitySelector key="density" />
                {view === 'mensal' && (
                    <GridMenuButton
                        key="organizar"
                        startIcon={
                            organizar === 'dia' ? (
                                <Calendar1Icon />
                            ) : (
                                <CalendarClockIcon />
                            )
                        }
                        label="Organizar"
                    >
                        {MenuItemsOrganizar}
                    </GridMenuButton>
                )}
                {view === 'diario' && <ExportarCsvAssinado key="csv" />}
            </Menu.Popover>
        </Menu.Root>,
        <div key="desktop" className="gap-1 hidden xl:inline-flex">
            {categoria !== CategoriaEnum.Funcionario && (
                <Tooltip key="filtro" title="Disposição dos filtros">
                    <div>
                        <GridMenuButton
                            startIcon={<ListFilterIcon />}
                            className="min-w-full"
                        >
                            {MenuItemsFiltro}
                        </GridMenuButton>
                    </div>
                </Tooltip>
            )}
            <Tooltip title="Densidade das linhas" key="density-tooltip">
                <div>
                    <ToolbarDensitySelector hideLabel />
                </div>
            </Tooltip>
            {view === 'mensal' && (
                <Tooltip key="organizar-tooltip" title="Organizar registros">
                    <div>
                        <GridMenuButton
                            startIcon={
                                organizar === 'dia' ? (
                                    <Calendar1Icon />
                                ) : (
                                    <CalendarClockIcon />
                                )
                            }
                        >
                            {MenuItemsOrganizar}
                        </GridMenuButton>
                    </div>
                </Tooltip>
            )}
            {view === 'diario' && (
                <Tooltip key="csv" title="Exportar CSV">
                    <div>
                        <ExportarCsvAssinado hideLabel />
                    </div>
                </Tooltip>
            )}
        </div>,
    ]
}

function CircleCheck(checked?: boolean) {
    return (
        <div className="rounded-full size-5 ring-2 ring-primary flex items-center justify-center mr-2">
            <div
                className={cn(
                    'rounded-full size-3',
                    checked ? 'bg-primary' : 'bg-transparent',
                )}
            />
        </div>
    )
}
